import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { AdminRole } from '../../types/constants.types';
import { getOwnerSetupStatus } from '../../services/OwnerSetupService';
import type { OwnerKioskSetupStatus } from '../../types/ownerSetup.types';
import OwnerSetupDialog from './OwnerSetupDialog';
import {
  OWNER_SETUP_COMPLETED_EVENT,
  OWNER_SETUP_OPEN_EVENT,
  beginOwnerSetupGate,
  settleOwnerSetupGate,
} from './ownerSetupEvents';

// If the status check hangs, release the tour rather than hold it forever.
const GATE_TIMEOUT_MS = 6000;

function OwnerSetupGateInner() {
  const [pending, setPending] = useState<OwnerKioskSetupStatus | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async (): Promise<OwnerKioskSetupStatus | null> => {
    try {
      const all = await getOwnerSetupStatus();
      const incomplete = all.find((kiosk) => !kiosk.complete && kiosk.ownerItems.some((item) => !item.satisfied)) || null;
      setPending(incomplete);
      return incomplete;
    } catch {
      return null; // Setup is a prompt, never a blocker: if the check fails, carry on to the dashboard.
    }
  }, []);

  // Ask straight after login (first render of the signed-in layout).
  useEffect(() => {
    let cancelled = false;
    beginOwnerSetupGate();
    const failSafe = window.setTimeout(settleOwnerSetupGate, GATE_TIMEOUT_MS);

    const decide = async () => {
      const incomplete = await load();
      if (cancelled) return;
      if (!incomplete) {
        settleOwnerSetupGate();
        return;
      }
      // The setup is mandatory: the dialog opens on every login until it is done, and the tour waits for it.
      window.clearTimeout(failSafe); // The fail-safe only covers the status check, not the owner's time in the dialog.
      setOpen(true);
    };
    void decide();

    return () => {
      cancelled = true;
      window.clearTimeout(failSafe);
      settleOwnerSetupGate();
    };
  }, [load]);

  // The Getting started panel can ask for the dialog again, e.g. if the first status check failed.
  useEffect(() => {
    const reopen = () => {
      void load().then((incomplete) => {
        if (incomplete) setOpen(true);
      });
    };
    window.addEventListener(OWNER_SETUP_OPEN_EVENT, reopen);
    return () => window.removeEventListener(OWNER_SETUP_OPEN_EVENT, reopen);
  }, [load]);

  const completed = () => {
    setOpen(false);
    setPending(null);
    settleOwnerSetupGate();
    window.dispatchEvent(new CustomEvent(OWNER_SETUP_COMPLETED_EVENT));
  };

  if (!open || !pending) return null;
  return <OwnerSetupDialog key={pending.kioskId} status={pending} onCompleted={completed} />;
}

/** Mounted for every signed-in user; only channel owners are ever asked. */
export default function OwnerSetupGate() {
  const role = useSelector((state: RootState) => state.auth.role);
  if (role !== AdminRole.KIOSK_OWNER) return null;
  return <OwnerSetupGateInner />;
}
